// app/javascript/controllers/context_status_controller.js
// Stimulus controller for real-time context status monitoring

import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

export default class extends Controller {
  static values = { 
    refreshInterval: Number,
    sessionId: String
  }
  
  connect() {
    console.log("Context status controller connected")
    
    // Start periodic refresh
    this.startRefreshing()
    
    // Subscribe to real-time updates
    this.subscribeToUpdates()
    
    // Listen for mode changes from chat controller
    document.addEventListener("claude-code:mode-changed", this.handleModeChange.bind(this))
  }
  
  disconnect() {
    this.stopRefreshing()
    this.unsubscribeFromUpdates()
    document.removeEventListener("claude-code:mode-changed", this.handleModeChange.bind(this))
  }
  
  startRefreshing() {
    if (this.hasRefreshIntervalValue && this.refreshIntervalValue > 0) {
      this.refreshTimer = setInterval(() => {
        this.refresh()
      }, this.refreshIntervalValue)
    }
  }
  
  stopRefreshing() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
      this.refreshTimer = null
    }
  }
  
  refresh() {
    // Fetch updated context status
    const sessionId = this.sessionIdValue || this.element.dataset.sessionId
    
    if (!sessionId) return
    
    fetch(`/claude_code/sessions/${sessionId}/context_status`, {
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
      }
    })
    .then(response => response.json())
    .then(data => this.updateDisplay(data))
    .catch(error => console.error("Error fetching context status:", error))
  }
  
  updateDisplay(data) {
    // Update progress bar
    const progressBar = this.element.querySelector("progress")
    if (progressBar) {
      progressBar.value = data.usage.percentage
      progressBar.className = `progress ${this.getProgressClass(data.usage.percentage)} w-full`
    }
    
    // Update token stats
    this.updateStat("used", this.formatTokens(data.usage.tokens))
    this.updateStat("remaining", this.formatTokens(data.usage.remaining))
    this.updateStat("turns", this.formatTurns(data.turns_estimate))
    
    // Update health badge
    const healthBadge = this.element.querySelector(".badge")
    if (healthBadge) {
      healthBadge.className = this.getHealthBadgeClass(data.health)
      healthBadge.textContent = this.getHealthLabel(data.health)
    }
    
    // Update warning if needed
    this.updateWarning(data)
    
    // Show/hide optimization actions
    this.toggleOptimizationActions(data.usage.percentage)
    
    // Dispatch event for other components
    this.dispatch("updated", { detail: data })
  }
  
  updateStat(type, value) {
    const element = this.element.querySelector(`[data-stat="${type}"]`)
    if (element) {
      element.textContent = value
    }
  }
  
  updateWarning(data) {
    const warningContainer = this.element.querySelector(".alert")
    
    if (data.health === "warning" || data.health === "critical") {
      if (!warningContainer) {
        // Create warning alert
        const alert = document.createElement("div")
        alert.className = data.health === "critical" ? "alert alert-error mt-3" : "alert alert-warning mt-3"
        alert.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-5 w-5" fill="none" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span class="text-xs">${this.getWarningMessage(data)}</span>
        `
        this.element.querySelector(".card-body").appendChild(alert)
      } else {
        // Update existing warning
        warningContainer.className = data.health === "critical" ? "alert alert-error mt-3" : "alert alert-warning mt-3"
        warningContainer.querySelector("span").textContent = this.getWarningMessage(data)
      }
      
      // Show notification if critical
      if (data.health === "critical" && !this.criticalNotified) {
        this.showNotification("Context Critical!", "Consider optimizing or resetting your session", "error")
        this.criticalNotified = true
      }
    } else if (warningContainer) {
      warningContainer.remove()
      this.criticalNotified = false
    }
  }
  
  toggleOptimizationActions(percentage) {
    const dropdown = this.element.querySelector(".dropdown")
    if (dropdown) {
      dropdown.style.display = percentage > 70 ? "block" : "none"
    }
  }
  
  subscribeToUpdates() {
    const sessionId = this.sessionIdValue || this.element.dataset.sessionId
    if (!sessionId) return
    
    this.subscription = consumer.subscriptions.create(
      { 
        channel: "ClaudeCodeContextChannel",
        session_id: sessionId
      },
      {
        received: (data) => {
          console.log("Received context update:", data)
          this.updateDisplay(data)
        }
      }
    )
  }
  
  unsubscribeFromUpdates() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }
  
  // Mode switching actions
  switchToIndependent(event) {
    event.preventDefault()
    this.switchMode("independent")
  }
  
  switchToContinue(event) {
    event.preventDefault()
    
    // Show warning before switching to continue mode
    if (confirm("Continue mode preserves ALL context across messages. This will cause context to accumulate rapidly. Are you sure?")) {
      this.switchMode("continue")
    }
  }
  
  switchMode(mode) {
    // Dispatch event for chat controller
    document.dispatchEvent(new CustomEvent("context-status:mode-change", {
      detail: { mode: mode }
    }))
    
    // Update UI immediately
    this.updateModeDisplay(mode)
    
    // Refresh status
    this.refresh()
  }
  
  updateModeDisplay(mode) {
    const independentBtn = this.element.querySelector('[data-action*="switchToIndependent"]')
    const continueBtn = this.element.querySelector('[data-action*="switchToContinue"]')
    
    if (independentBtn && continueBtn) {
      if (mode === "independent") {
        independentBtn.classList.add("btn-primary")
        independentBtn.classList.remove("btn-ghost")
        continueBtn.classList.remove("btn-primary")
        continueBtn.classList.add("btn-ghost")
      } else {
        continueBtn.classList.add("btn-primary")
        continueBtn.classList.remove("btn-ghost")
        independentBtn.classList.remove("btn-primary")
        independentBtn.classList.add("btn-ghost")
      }
    }
    
    // Update mode icon and label
    const modeIcon = this.element.querySelector(".text-2xl")
    const modeLabel = this.element.querySelector("h3")
    const modeDesc = this.element.querySelector(".text-base-content/60")
    
    if (modeIcon) {
      modeIcon.textContent = mode === "continue" ? "🔗" : "📦"
    }
    if (modeLabel) {
      modeLabel.textContent = mode === "continue" ? "Continuing Conversation" : "Independent Mode"
      modeLabel.className = `text-sm font-semibold ${mode === "continue" ? "text-warning" : "text-info"}`
    }
    if (modeDesc) {
      modeDesc.textContent = mode === "continue" ? "All context preserved" : "Minimal context"
    }
  }
  
  handleModeChange(event) {
    this.updateModeDisplay(event.detail.mode)
    this.refresh()
  }
  
  // Optimization actions
  summarizeHistory(event) {
    event.preventDefault()
    
    if (confirm("This will summarize older messages to reduce context usage. Continue?")) {
      this.performOptimization("summarize")
    }
  }
  
  pruneToolResults(event) {
    event.preventDefault()
    
    if (confirm("This will remove old file reads and tool outputs from context. Continue?")) {
      this.performOptimization("prune_tools")
    }
  }
  
  resetSession(event) {
    event.preventDefault()
    
    if (confirm("This will start a fresh session with a summary of current work. All conversation history will be archived. Continue?")) {
      this.performOptimization("reset")
    }
  }
  
  performOptimization(strategy) {
    const sessionId = this.sessionIdValue || this.element.dataset.sessionId
    
    fetch(`/claude_code/sessions/${sessionId}/optimize_context`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
      },
      body: JSON.stringify({ strategy: strategy })
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        this.showNotification("Optimization Complete", data.message, "success")
        this.refresh()
        
        // If reset, might need to reload page
        if (strategy === "reset" && data.new_session_id) {
          window.location.href = `/claude_code/sessions/${data.new_session_id}`
        }
      } else {
        this.showNotification("Optimization Failed", data.error, "error")
      }
    })
    .catch(error => {
      console.error("Optimization error:", error)
      this.showNotification("Error", "Failed to optimize context", "error")
    })
  }
  
  // Helper methods
  formatTokens(count) {
    if (!count || count === 0) return "0"
    
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`
    }
    return count.toString()
  }
  
  formatTurns(turns) {
    if (!turns) return "Unknown"
    if (turns >= 999) return "Unlimited"
    if (turns <= 1) return "< 1 turn"
    if (turns <= 5) return `~${turns} turns`
    return `${turns}+ turns`
  }
  
  getProgressClass(percentage) {
    if (percentage >= 85) return "progress-error"
    if (percentage >= 70) return "progress-warning"
    if (percentage >= 50) return "progress-info"
    return "progress-success"
  }
  
  getHealthBadgeClass(health) {
    switch(health) {
      case "healthy": return "badge badge-success"
      case "good": return "badge badge-info"
      case "warning": return "badge badge-warning"
      case "critical": return "badge badge-error animate-pulse"
      default: return "badge"
    }
  }
  
  getHealthLabel(health) {
    switch(health) {
      case "healthy": return "Healthy"
      case "good": return "Good"
      case "warning": return "Warning"
      case "critical": return "Critical"
      default: return "Unknown"
    }
  }
  
  getWarningMessage(data) {
    if (data.health === "critical") {
      return "Context usage critical! Consider resetting or optimizing."
    } else if (data.health === "warning") {
      return `Context usage high. ${this.formatTurns(data.turns_estimate)} remaining.`
    }
    return ""
  }
  
  showNotification(title, message, type = "info") {
    // Dispatch event for toast notification
    document.dispatchEvent(new CustomEvent("notification:show", {
      detail: {
        title: title,
        message: message,
        type: type
      }
    }))
  }
  
  dispatch(eventName, options = {}) {
    this.element.dispatchEvent(new CustomEvent(`context-status:${eventName}`, options))
  }
}